<template>
  <div class="flex flex-row w-full">
    <div class="flex-grow max-w-[80%]">
      {{ text.name }}<br />

      <div class="flex space-x-1 pt-2 px-1">
        <button type="button" @click="toggleBold">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
            stroke="currentColor" class="text-gray-600 cursor-pointer w-4 h-4 hover:text-brandhelfer-blue">
            <path stroke-linejoin="round"
              d="M6.75 3.744h-.753v8.25h7.125a4.125 4.125 0 0 0 0-8.25H6.75Zm0 0v.38m0 16.122h6.747a4.5 4.5 0 0 0 0-9.001h-7.5v9h.753Zm0 0v-.37m0-15.751h6a3.75 3.75 0 1 1 0 7.5h-6m0-7.5v7.5m0 0v8.25m0-8.25h6.375a4.125 4.125 0 0 1 0 8.25H6.75m.747-15.38h4.875a3.375 3.375 0 0 1 0 6.75H7.497v-6.75Zm0 7.5h5.25a3.75 3.75 0 0 1 0 7.5h-5.25v-7.5Z" />
          </svg>
        </button>
      </div>

      <editor-content v-if="editor" :editor="editor" @paste="handlePaste"
        class="editor w-full h-full text-inherit resize-none overflow-auto py-2 px-3 bg-white block sm:text-sm"
        :class="[smallscreen ? 'text-xs' : '']" />




      <div v-if="hasTextColors" class="mt-2">
        <div v-for="(color, idx) in textColors" :key="idx" :style="`background-color: rgba(${color},1)`"
          @click="changeTextColor(color)"
          class="w-2 h-2 rounded float-left border-2 hover:border-brandhelfer-green cursor-pointer" :class="[
            smallscreen ? 'p-3 mr-1 mb-1' : 'p-4 mr-2 mb-2',
            selectedColor == color ? 'border-brandhelfer-green cursor-default' : 'border-gray-200'
          ]">
        </div>
      </div>
    </div>
    <div class="w-1/5">
      <toggle :idx="text.id" :enabled="text.enabled" @toggled="statusToggled"></toggle>
    </div>

    <horizontal-divider />
  </div>
</template>

<script>
import Toggle from '@/Shared/Toggle.vue'
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import TextStyle from '@tiptap/extension-text-style'
// import Color from '@tiptap/extension-color'
import FontFamily from '@tiptap/extension-font-family'

export default {
  components: {
    Toggle,
    EditorContent,
  },

  props: {
    text: Object,
    smallscreen: { default: false, required: false },
  },

  data() {
    return {
      editor: null,
      selectedColor: this.text?.color ?? this.text?.defaultColor ?? '',
      lastContentSet: null,
      isUpdatingFromOutside: false,
    }
  },

  mounted() {
    const raw = this.text?.value ?? this.placeholder() ?? ''
    const initial = this.normalizeForTiptap(raw)

    this.editor = new Editor({
      extensions: [
        StarterKit,
        TextStyle,
        FontFamily,
      ],
      content: initial,
      onUpdate: ({ editor }) => {
        if (this.isUpdatingFromOutside) return
        const html = editor.getHTML()
          .replace(/<p>/g, '<div>')
          .replace(/<\/p>/g, '</div>')
        this.$emit('textEntered', this.text, html)
      },
    })

    this.lastContentSet = this.editor.getHTML()
  },

  beforeDestroy() {
    this.editor?.destroy()
  },

  watch: {
    'text.value'(newValue) {
      if (!this.editor) return
      const normalized = this.normalizeForTiptap(newValue ?? '')
      const current = this.editor.getHTML()
      if (normalized !== current) {
        this.isUpdatingFromOutside = true
        this.editor.commands.setContent(normalized, false)
        this.isUpdatingFromOutside = false
        this.lastContentSet = normalized
      }
    },

    selectedColor(newVal) {
      this.$emit('textColorChanged', this.text, newVal)
    },
  },

  methods: {
    normalizeForTiptap(html) {
      if (!html) return '<p></p>'
      const trimmed = String(html).trim()
      // TipTap wants block nodes at the root:
      const hasBlockRoot = /^<(p|h[1-6]|ul|ol|blockquote|pre|div)\b/i.test(trimmed)
      
      return hasBlockRoot ? trimmed : `<p>${trimmed}</p>`
    },

    toggleBold() {
      this.editor.chain().focus().toggleBold().run()
    },

    changeTextColor(color) {
      this.selectedColor = color
    },

    statusToggled(idx, status) {
      this.$emit('textStatusToggled', this.text, status)
    },

    placeholder() {
      if (typeof this.text.placeholder === 'object' && this.text.placeholder?.type === 'current_date') {
        return new Date(Date.now()).toLocaleString('de-DE').split(',')[0]
      }
      return this.text.placeholder
    },

    handlePaste(event) {
      event.preventDefault()
      const txt = (event.clipboardData || window.clipboardData).getData('text') || ''
      if (!txt) return
      const cleanHtml = txt
        .split(/\r\n|\r|\n/)
        .map(line => line.trim())
        .filter(Boolean)
        .join('<br />')
      this.editor.commands.insertContent(cleanHtml)
    },
  },

  computed: {
    textColors() {
      return this.text?.colors || []
    },

    hasTextColors() {
      return (this.textColors?.length || 0) > 1
    },

    rows() {
      return this.text?.rows || 3
    },

    fieldHeight() {
      return `height: ${this.rows * 40}px`
    },
  },
}

</script>


<style scoped>
.editor :focus-visible {
  outline: none;
  box-shadow: none;
}

.editor {
  border: none !important;
  outline: none !important;
}
</style>